"use client";

import Image from "next/image";
import { Check, X } from "lucide-react";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { borderRadius } from "@/utils/borderRadius";
import { assertTruthy } from "@knowt/syncing/utils/assertions";
import { ASSETS_URL, SSO_LOGIN_URL } from "@/config/deployConstants";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { getPlanName } from "@knowt/syncing/hooks/user/subscriptionConstants";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumn, FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import { capitalize } from "@knowt/syncing/utils/stringUtils";
import InitialSetupContainer from "../styled/IntialSetupContainer";

const features = [
    {
        isChecked: true,
        text: "No ads",
    },
    {
        isChecked: true,
        text: "Unlimited explanations on incorrect answers during Learn Mode",
    },
    {
        isChecked: true,
        text: "Customization & password protecting",
    },
];

const LinkSSOAccountStep = () => {
    const { subscriptionType, organization } = useCurrentUser();

    const _onSubmit = () => {
        window.location.href = SSO_LOGIN_URL;
    };

    assertTruthy(organization, "Organization should be defined");

    return (
        <InitialSetupContainer style={{ gap: spacing.LG_2 }}>
            <FlexColumn
                style={{
                    backgroundColor: themeColors.neutralWhite,
                    borderRadius: borderRadius.card,
                    gap: "4rem",
                }}>
                <Image src={`${ASSETS_URL}/images/crown.svg`} alt="Crown icon" width={124} height={124} />
                <FlexColumn>
                    <h1
                        className="heading4"
                        data-testid="auto-id-1861a6bf-7143-4d71-a9c2-93d11e762b39">
                        You’re on the {getPlanName(subscriptionType)} plan! Sign in with{" "}
                        <b>{capitalize(organization.provider)}</b> below so we can link the subscription to your
                        account.
                    </h1>
                    <p
                        className="body2"
                        data-testid="auto-id-5a81207c-1b66-41df-83a4-bc62064880c7">
                        Your school is awesome -- they already bought you the plus plan and now you’ll be able to do
                        some pretty cool things. Just log into the portal below to claim it.
                    </p>
                </FlexColumn>

                <FlexColumn
                    style={{
                        border: `solid 1px ${themeColors.neutralBlack}`,
                        padding: spacing.SM,
                        borderRadius: borderRadius.card,
                        width: "fit-content",
                        gap: spacing.XS_2,
                        maxWidth: "80%",
                    }}>
                    {features.map((feature, index) => (
                        <FlexRowAlignCenter key={index} style={{ gap: spacing.XS_2 }}>
                            <FlexColumnAlignJustifyCenter
                                style={{
                                    backgroundColor: feature.isChecked
                                        ? themeColors.neutralBlack
                                        : themeColors.errorPrimary,
                                    width: "fit-content",
                                    borderRadius: "50%",
                                    padding: spacing.XXS,
                                }}>
                                {feature.isChecked ? (
                                    <Check size={24} color={themeColors.neutralWhite} />
                                ) : (
                                    <X size={24} color={themeColors.neutralWhite} />
                                )}
                            </FlexColumnAlignJustifyCenter>
                            <span
                                className="body2"
                                data-testid="auto-id-c6a049b6-b8a2-4f95-94eb-14e26d50a59f">{feature.text}</span>
                        </FlexRowAlignCenter>
                    ))}
                </FlexColumn>

                <FlexColumnAlignJustifyCenter style={{ alignItems: "flex-end" }}>
                    <CircularRectTextButton
                        onClick={_onSubmit}
                        style={{
                            backgroundColor: themeColors.primary,
                            color: themeColors.primaryDark,
                            padding: `${spacing.XS_2} ${spacing.MD}`,
                        }}>
                        {"Sign in to claim!"}
                    </CircularRectTextButton>
                </FlexColumnAlignJustifyCenter>
            </FlexColumn>
        </InitialSetupContainer>
    );
};

export default LinkSSOAccountStep;
